import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Menu, 
  X, 
  Home,
  Wrench,
  Layers,
  Newspaper,
  BookOpen,
  ExternalLink,
  ChevronDown,
  Map,
  Users,
  Mail,
  HelpCircle
} from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import LanguageToggle from './LanguageToggle';
import { useLanguage } from './LanguageContext';
import { useModal } from './ModalContext';
import { 
  whataLabLogo, 
  hederaLogo, 
  wattImage, 
  earlyPackImage, 
  wcaseGif,
  wboostIcon,
  spotifyIcon
} from '../assets';
import { EXTERNAL_LINKS } from '../config';
import './Sidebar.css';

// Mapeo de nombres de iconos a componentes
const iconMap = {
  Home,
  Wrench,
  Layers,
  Newspaper,
  BookOpen,
  Map,
  Users,
  Mail,
  HelpCircle,
};

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [toolsExpanded, setToolsExpanded] = useState(false);
  const [ecosystemExpanded, setEcosystemExpanded] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { t } = useLanguage();
  const { openWhataKit, openEarlyPack, openWatt } = useModal();

  // Navigation items with translation keys
  const navItems = [
    { key: 'home',       href: '#home',      icon: 'Home'        },
    { key: 'tools',      href: '#tools',     icon: 'Wrench',     hasSubmenu: 'tools'      },
    { key: 'ecosystem',  href: '#ecosystem', icon: 'Layers',     hasSubmenu: 'ecosystem'  },
    { key: 'roadmap',    href: '#about',     icon: 'Map',        label: 'Roadmap'         },
    { key: 'team',       href: '#team',      icon: 'Users',      label: 'Team'            },
    { key: 'faqs',       href: '#faqs',      icon: 'HelpCircle', label: 'FAQs'            },
    { key: 'contact',    href: '#contact',   icon: 'Mail',       label: 'Contact'         },
    { key: 'whataSpace', href: EXTERNAL_LINKS.SPOTIFY,   customIcon: spotifyIcon, external: true },
    { key: 'whataNews',  href: EXTERNAL_LINKS.WHATANEWS, icon: 'Newspaper',       external: true },
  ];

  // Submenú de Tools
  const toolsSubmenu = [
    { key: 'wPanel', href: EXTERNAL_LINKS.PANEL, label: 'W·Panel', icon: 'P', external: true },
    { key: 'wKit', href: EXTERNAL_LINKS.TOOLS_PLATFORM, label: 'W·Kit', icon: 'W', external: true },
  ];

  // Submenú de Ecosystem
  const ecosystemSubmenu = [
    { key: 'watt', label: 'WATT', iconImg: wattImage, action: openWatt },
    { key: 'earlyPack', label: 'Early Pack', iconImg: earlyPackImage, action: openEarlyPack },
    { key: 'whataKit', label: 'WhataKit', iconImg: wcaseGif, action: openWhataKit },
    { key: 'wBoost', href: EXTERNAL_LINKS.TOOLS_BUY, label: 'W·Boost', iconImg: wboostIcon, external: true },
  ];

  // Track scroll for mobile header shadow
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close sidebar on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Update body class when sidebar is expanded (desktop only)
  useEffect(() => {
    if (isHovered && window.innerWidth > 1024) {
      document.body.classList.add('sidebar-expanded');
    } else {
      document.body.classList.remove('sidebar-expanded');
    }
    return () => {
      document.body.classList.remove('sidebar-expanded');
    };
  }, [isHovered]);

  const handleNavClick = () => {
    setIsOpen(false);
  };

  const handleSubmenuToggle = (submenuType, sectionId) => (e) => {
    e.preventDefault();
    
    // Toggle del submenú
    if (submenuType === 'tools') {
      setToolsExpanded(!toolsExpanded);
    } else if (submenuType === 'ecosystem') {
      setEcosystemExpanded(!ecosystemExpanded);
    }
    
    // Navegar a la sección con un pequeño delay para asegurar el scroll
    setTimeout(() => {
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleSubmenuItemClick = (item) => (e) => {
    if (item.action) {
      e.preventDefault();
      item.action();
      setIsOpen(false); // Cerrar sidebar en mobile
    }
  };

  const isExpanded = isOpen || isHovered;

  const getSubmenuData = (submenuType) => {
    if (submenuType === 'tools') return { items: toolsSubmenu, expanded: toolsExpanded };
    if (submenuType === 'ecosystem') return { items: ecosystemSubmenu, expanded: ecosystemExpanded };
    return null;
  };

  return (
    <>
      {/* Mobile Header - Fijo en la parte superior */}
      <header className={`mobile-header ${isScrolled ? 'mobile-header--scrolled' : ''}`}>
        <button
          className="mobile-header-toggle"
          onClick={() => setIsOpen(true)}
          aria-label={t('common.openMenu')}
        >
          <Menu size={20} />
        </button>
        <a href="#home" className="mobile-header-logo">
          <div className="mobile-header-logo-sphere">
            <img src={whataLabLogo} alt="WhataLab" />
          </div>
          <span className="mobile-header-logo-text">WhataLab</span>
        </a>
      </header>

      {/* Overlay for mobile */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="sidebar-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside
        className={`sidebar ${isOpen ? 'sidebar--open' : ''} ${isExpanded ? 'sidebar--expanded' : ''}`}
        onMouseEnter={() => {
          // Solo hover en desktop
          if (window.innerWidth > 1024) setIsHovered(true);
        }}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Header con logo y botón cerrar */}
        <div className="sidebar-header">
          {/* Logo */}
          <a href="#home" className="sidebar-logo" onClick={handleNavClick}>
            <div className="sidebar-logo-sphere">
              <div className="sidebar-logo-icon">
                <img src={whataLabLogo} alt="WhataLab" />
              </div>
            </div>
            <span className="sidebar-logo-text">WhataLab</span>
          </a>

          {/* Botón cerrar - Solo visible en mobile cuando está abierto */}
          <button
            className="sidebar-close"
            onClick={() => setIsOpen(false)}
            aria-label={t('common.closeMenu')}
          >
            <X size={16} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          <ul className="sidebar-nav-list">
            {navItems.map((item) => {
              const IconComponent = iconMap[item.icon];
              const translatedName = item.label || t(`nav.${item.key}`);
              const submenuData = item.hasSubmenu ? getSubmenuData(item.hasSubmenu) : null;
              
              // Si tiene submenú
              if (item.hasSubmenu && submenuData) {
                return (
                  <li key={item.key} className="sidebar-nav-item--has-submenu">
                    <a
                      href={item.href}
                      className={`sidebar-nav-link ${submenuData.expanded ? 'sidebar-nav-link--active' : ''}`}
                      onClick={handleSubmenuToggle(item.hasSubmenu, item.key === 'tools' ? 'tools' : 'ecosystem')}
                      title={translatedName}
                    >
                      {IconComponent && <IconComponent size={20} className="sidebar-nav-icon" />}
                      <span className="sidebar-nav-text">{translatedName}</span>
                      <ChevronDown 
                        size={14} 
                        className={`sidebar-nav-chevron ${submenuData.expanded ? 'sidebar-nav-chevron--open' : ''}`} 
                      />
                    </a>
                    
                    {/* Submenú */}
                    <AnimatePresence>
                      {submenuData.expanded && (
                        <motion.ul
                          className="sidebar-submenu"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          {submenuData.items.map((subItem) => (
                            <li key={subItem.key}>
                              {subItem.external ? (
                                <a
                                  href={subItem.href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="sidebar-submenu-link"
                                  title={subItem.label}
                                >
                                  <span className="sidebar-submenu-icon">
                                    {subItem.iconImg ? (
                                      <img src={subItem.iconImg} alt="" className="sidebar-submenu-icon-img" />
                                    ) : subItem.icon}
                                  </span>
                                  <span className="sidebar-submenu-text">{subItem.label}</span>
                                  <ExternalLink size={10} className="sidebar-submenu-external" />
                                </a>
                              ) : (
                                <button
                                  className="sidebar-submenu-link sidebar-submenu-link--button"
                                  onClick={handleSubmenuItemClick(subItem)}
                                  title={subItem.label}
                                >
                                  <span className="sidebar-submenu-icon">
                                    {subItem.iconImg ? (
                                      <img src={subItem.iconImg} alt="" className="sidebar-submenu-icon-img" />
                                    ) : subItem.icon}
                                  </span>
                                  <span className="sidebar-submenu-text">{subItem.label}</span>
                                </button>
                              )}
                            </li>
                          ))}
                        </motion.ul>
                      )}
                    </AnimatePresence>
                  </li>
                );
              }
              
              // Props comunes para el link normal
              const linkProps = {
                href: item.href,
                className: `sidebar-nav-link ${item.external ? 'sidebar-nav-link--external' : ''}`,
                title: translatedName,
                ...(item.external ? {
                  target: '_blank',
                  rel: 'noopener noreferrer'
                } : {
                  onClick: handleNavClick
                })
              };
              
              return (
                <li key={item.key}>
                  <a {...linkProps}>
                    {item.customIcon ? (
                      <img src={item.customIcon} alt="" className="sidebar-nav-icon sidebar-nav-icon--custom" />
                    ) : (
                      IconComponent && <IconComponent size={20} className="sidebar-nav-icon" />
                    )}
                    <span className="sidebar-nav-text">{translatedName}</span>
                    {item.external && <ExternalLink size={12} className="sidebar-nav-external" />}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Bottom Section */}
        <div className="sidebar-bottom">
          {/* Toggles: Theme & Language */}
          <div className="sidebar-toggles">
            <ThemeToggle />
            <LanguageToggle />
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
